import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { useAuthUser } from "#/api/auth";
import {
  useCreateSession,
  useContinueSession,
  SessionLimitReachedError,
  SessionDailyCapError,
  type AIResponse,
} from "#/api/sessions";
import {
  formatNextFreeAt,
  useBillingStatus,
} from "#/api/billing";
import type { PriorResolution } from "@myapp/shared";
import { LogoMark } from "#/components/LogoMark";
import { UpgradeSlide } from "#/components/UpgradeSlide";
import {
  ArrowDown,
  RotateCcw,
  CheckCircle2,
  ChevronRight,
  Sparkles,
  X,
} from "lucide-react";

export const Route = createFileRoute("/")({
  component: Home,
});

type Slide = {
  id: string;
  stage: AIResponse["stage"];
  ai: AIResponse;
  userResponse?: string;
};

const STAGE_LABELS: { key: AIResponse["stage"]; label: string }[] = [
  { key: "own_fear", label: "Own" },
  { key: "amplify_fear", label: "Amplify" },
  { key: "discover_belief", label: "Discover" },
  { key: "see_through", label: "See" },
  { key: "choose_belief", label: "Choose" },
  { key: "embody", label: "Embody" },
  { key: "complete", label: "Complete" },
];

function stageIndex(stage: AIResponse["stage"]) {
  return STAGE_LABELS.findIndex((s) => s.key === stage);
}

function Progress({ stage }: { stage: AIResponse["stage"] }) {
  const idx = stageIndex(stage);
  return (
    <div className="pointer-events-none flex w-full max-w-xl items-center justify-center">
      {STAGE_LABELS.map((s, i) => (
        <div key={s.key} className="flex items-center">
          <div className="flex flex-col items-center">
            <div
              className={`h-2 w-2 rounded-full transition-colors ${
                i <= idx ? "bg-primary" : "bg-white/15"
              }`}
            />
            <span
              className={`mt-1.5 hidden text-[9px] uppercase tracking-widest md:block ${
                i <= idx ? "text-primary/90" : "text-muted-foreground/50"
              }`}
            >
              {s.label}
            </span>
          </div>
          {i < STAGE_LABELS.length - 1 && (
            <div
              className={`mx-1 h-px w-2 md:mx-1.5 md:w-3 ${i < idx ? "bg-primary/40" : "bg-white/10"}`}
            />
          )}
        </div>
      ))}
    </div>
  );
}

function ReelContainer({
  children,
  containerRef,
}: {
  children: React.ReactNode;
  containerRef: React.RefObject<HTMLDivElement | null>;
}) {
  return (
    <div
      ref={containerRef}
      className="h-dvh w-full snap-y snap-mandatory overflow-y-scroll scroll-smooth"
    >
      {children}
    </div>
  );
}

function Slide({
  children,
  navOnly = false,
}: {
  children: React.ReactNode;
  navOnly?: boolean;
}) {
  return (
    <section
      className={
        navOnly
          ? "flex min-h-dvh w-full snap-start flex-col items-center justify-center px-4 pb-[max(1.5rem,env(safe-area-inset-bottom))] pt-[calc(env(safe-area-inset-top,0px)+var(--nav-height))] md:px-6 md:py-24"
          : "flex h-dvh w-full snap-start flex-col items-center overflow-y-auto px-4 pb-[max(2rem,env(safe-area-inset-bottom))] pt-[var(--content-top)] md:justify-center md:px-6 md:pb-24"
      }
    >
      <div className="flex w-full max-w-md flex-col items-center py-4 md:py-0">{children}</div>
    </section>
  );
}

function Message({ text }: { text: string }) {
  return (
    <div className="mb-6 max-w-xl text-center md:mb-8">
      <p className="whitespace-pre-line font-serif text-xl font-light leading-relaxed text-foreground md:text-2xl lg:text-3xl">
        {text}
      </p>
    </div>
  );
}

function PrimaryButton({
  onClick,
  disabled,
  children,
}: {
  onClick: () => void;
  disabled?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className="inline-flex items-center gap-2 rounded-full bg-white/10 px-6 py-3 text-sm font-medium text-primary backdrop-blur-sm transition-all hover:bg-white/[0.14] hover:shadow-[0_0_24px_rgba(180,160,220,0.2)] disabled:opacity-40 disabled:hover:shadow-none"
    >
      {children}
    </button>
  );
}

function TextInput({
  value,
  onChange,
  onSubmit,
  placeholder,
  disabled,
}: {
  value: string;
  onChange: (v: string) => void;
  onSubmit: () => void;
  placeholder: string;
  disabled?: boolean;
}) {
  return (
    <div className="w-full">
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            onSubmit();
          }
        }}
        placeholder={placeholder}
        disabled={disabled}
        className="min-h-[120px] w-full resize-none rounded-2xl border border-white/10 bg-white/[0.04] p-4 font-sans text-base text-foreground placeholder:text-muted-foreground/60 focus:border-primary/60 focus:bg-white/[0.06] focus:outline-none disabled:opacity-50 md:min-h-[140px] md:p-5"
      />
      <div className="mt-4 flex justify-end">
        <PrimaryButton onClick={onSubmit} disabled={disabled || !value.trim()}>
          Continue
          <ChevronRight className="h-4 w-4" />
        </PrimaryButton>
      </div>
    </div>
  );
}

function OptionCard({
  option,
  selected,
  onSelect,
}: {
  option: string;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      onClick={onSelect}
      className={`w-full rounded-2xl border p-3 md:p-4 text-left transition-all ${
        selected
          ? "border-primary/70 bg-primary/10"
          : "border-white/10 bg-white/[0.04] hover:border-white/20 hover:bg-white/[0.06]"
      }`}
    >
      <div className="flex items-start gap-3">
        <div
          className={`mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full border-2 ${
            selected ? "border-primary bg-primary" : "border-white/30"
          }`}
        >
          {selected && <CheckCircle2 className="h-3.5 w-3.5 text-background" />}
        </div>
        <p className="text-sm leading-relaxed text-foreground/90">{option}</p>
      </div>
    </button>
  );
}

function OptionsSlide({
  ai,
  onSubmit,
  disabled,
}: {
  ai: AIResponse;
  onSubmit: (response: string) => void;
  disabled?: boolean;
}) {
  const [selected, setSelected] = useState<string | null>(null);
  const [custom, setCustom] = useState("");

  const response = selected ?? custom;

  const options = ai.allowCustom
    ? ai.options.filter((opt) => opt.trim().toLowerCase() !== "write my own")
    : ai.options;

  return (
    <Slide>
      {ai.question ? (
        <>
          {ai.message && (
            <p className="mb-4 max-w-md text-center text-sm leading-relaxed text-muted-foreground">
              {ai.message}
            </p>
          )}
          <Message text={ai.question} />
        </>
      ) : (
        <Message text={ai.message} />
      )}
      <div className="w-full space-y-2 md:space-y-3">
        {options.map((opt) => (
          <OptionCard
            key={opt}
            option={opt}
            selected={selected === opt}
            onSelect={() => setSelected(opt)}
          />
        ))}
        {ai.allowCustom && (
          <div className="pt-1 md:pt-2">
            <p className="mb-2 text-xs text-muted-foreground">
              Or write your own:
            </p>
            <textarea
              value={custom}
              onChange={(e) => {
                setCustom(e.target.value);
                setSelected(null);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey && response.trim()) {
                  e.preventDefault();
                  onSubmit(response.trim());
                }
              }}
              placeholder="My own reflection..."
              disabled={disabled}
              className="min-h-[80px] w-full resize-none rounded-2xl border border-white/10 bg-white/[0.04] p-4 text-sm text-foreground placeholder:text-muted-foreground/60 focus:border-primary/60 focus:outline-none disabled:opacity-50"
            />
          </div>
        )}
        <div className="flex justify-end pt-3 md:pt-4">
          <PrimaryButton
            onClick={() => onSubmit(response.trim())}
            disabled={disabled || !response.trim()}
          >
            Continue
            <ChevronRight className="h-4 w-4" />
          </PrimaryButton>
        </div>
      </div>
    </Slide>
  );
}

function IntroSlide({
  onStart,
  disabled,
  freeStatusLine,
}: {
  onStart: () => void;
  disabled?: boolean;
  freeStatusLine?: string | null;
}) {
  return (
    <Slide navOnly>
      <div className="mb-6 md:mb-8">
        <LogoMark size="lg" />
      </div>
      <h1 className="mb-4 max-w-lg text-center font-serif text-3xl font-light leading-tight text-foreground sm:text-4xl md:text-6xl">
        Your fear is a <span className="italic text-primary">messenger</span>
      </h1>
      <p className="mb-10 max-w-md text-center text-base leading-relaxed text-muted-foreground">
        Own one fear, find the story behind it, and choose a new belief. Leave
        with a sacred mantra and a practice you can live today.
      </p>
      <PrimaryButton onClick={onStart} disabled={disabled}>
        <Sparkles className="h-4 w-4" />
        Begin transformation
      </PrimaryButton>
      {freeStatusLine && (
        <p className="mt-4 max-w-sm text-center text-xs text-muted-foreground/80">
          {freeStatusLine}
        </p>
      )}
      <div className="mt-12 text-muted-foreground/40">
        <ArrowDown className="h-5 w-5 animate-bounce" />
      </div>
    </Slide>
  );
}

function IneyaIntroSlide({
  onContinue,
  disabled,
}: {
  onContinue: () => void;
  disabled?: boolean;
}) {
  return (
    <Slide navOnly>
      <Message text="I'm Ineya, and I'll walk with you through one fear today. Fear isn't your enemy — it's a messenger, pointing to a belief that no longer serves you." />
      <PrimaryButton onClick={onContinue} disabled={disabled}>
        Continue
        <ChevronRight className="h-4 w-4" />
      </PrimaryButton>
    </Slide>
  );
}

function InputSlide({
  ai,
  onSubmit,
  disabled,
}: {
  ai: AIResponse;
  onSubmit: (response: string) => void;
  disabled?: boolean;
}) {
  const [value, setValue] = useState("");
  return (
    <Slide>
      <Message text={ai.message} />
      <TextInput
        value={value}
        onChange={setValue}
        onSubmit={() => onSubmit(value.trim())}
        placeholder="I'm afraid that..."
        disabled={disabled}
      />
    </Slide>
  );
}

function EmbodySlide({
  ai,
  onSubmit,
  disabled,
}: {
  ai: AIResponse;
  onSubmit: () => void;
  disabled?: boolean;
}) {
  return (
    <Slide>
      <p className="mb-3 text-xs uppercase tracking-widest text-primary/80">
        Daily practice
      </p>
      <Message text={ai.message} />
      {ai.sacredMantra && (
        <div className="mb-8 rounded-3xl border border-primary/30 bg-primary/10 px-8 py-5 text-center">
          <p className="mb-1 text-xs uppercase tracking-widest text-primary/70">
            Your sacred mantra
          </p>
          <p className="font-serif text-2xl italic text-primary">
            {ai.sacredMantra}
          </p>
        </div>
      )}
      <div className="w-full space-y-3">
        {ai.dailyActions?.map((action, i) => {
          const colonIndex = action.indexOf(":");
          const hasCategory = colonIndex > 0 && colonIndex < 25;
          const title = hasCategory ? action.slice(0, colonIndex).trim() : null;
          const description = hasCategory
            ? action.slice(colonIndex + 1).trim()
            : action;
          return (
            <div
              key={i}
              className="rounded-2xl border border-white/10 bg-white/[0.04] p-4"
              style={{ animationDelay: `${i * 0.1}s` }}
            >
              {title && (
                <p className="mb-1 text-xs font-medium uppercase tracking-wider text-primary/80">
                  {title}
                </p>
              )}
              <p className="text-sm leading-relaxed text-foreground/90">
                {description}
              </p>
            </div>
          );
        })}
      </div>
      <div className="mt-8 flex w-full justify-end">
        <PrimaryButton onClick={onSubmit} disabled={disabled}>
          Continue
          <ChevronRight className="h-4 w-4" />
        </PrimaryButton>
      </div>
    </Slide>
  );
}

function CompleteSlide({
  ai,
  onRestart,
}: {
  ai: AIResponse;
  onRestart: () => void;
}) {
  const summary = ai.sessionSummary;
  return (
    <Slide>
      <div className="mb-6">
        <LogoMark size="lg" />
      </div>
      <Message text={ai.message} />
      {summary && (
        <div className="w-full max-w-md space-y-4 rounded-3xl border border-white/10 bg-white/[0.04] p-6 backdrop-blur-sm">
          <div>
            <p className="text-xs uppercase tracking-widest text-muted-foreground">
              The fear that knocked
            </p>
            <p className="font-serif text-lg text-foreground">
              {summary.originalFear}
            </p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-widest text-muted-foreground">
              The story behind it
            </p>
            <p className="font-serif text-lg text-foreground/60 line-through decoration-primary/50">
              {summary.limitingBelief}
            </p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-widest text-primary/80">
              New belief
            </p>
            <p className="font-serif text-xl text-primary">
              {summary.empoweringBelief}
            </p>
          </div>
          {summary.sacredMantra && (
            <div>
              <p className="text-xs uppercase tracking-widest text-primary/80">
                Sacred mantra
              </p>
              <p className="font-serif text-lg italic text-primary/90">
                {summary.sacredMantra}
              </p>
            </div>
          )}
          <div className="border-t border-white/10 pt-4">
            <p className="text-xs uppercase tracking-widest text-muted-foreground">
              Key insight
            </p>
            <p className="text-sm italic leading-relaxed text-foreground/80">
              {summary.keyInsight}
            </p>
          </div>
        </div>
      )}
      <div className="mt-10">
        <PrimaryButton onClick={onRestart}>
          <RotateCcw className="h-4 w-4" />
          Transform another belief
        </PrimaryButton>
      </div>
    </Slide>
  );
}

function PriorResolutionBanner({
  resolution,
  onDismiss,
}: {
  resolution: PriorResolution;
  onDismiss: () => void;
}) {
  return (
    <div className="fixed bottom-[max(1.5rem,env(safe-area-inset-bottom))] left-1/2 z-50 w-full max-w-md -translate-x-1/2 px-4">
      <div className="relative rounded-2xl border border-primary/30 bg-primary/[0.08] px-5 py-4 backdrop-blur-md">
        <button
          onClick={onDismiss}
          aria-label="Dismiss"
          className="absolute right-3 top-3 text-muted-foreground transition-colors hover:text-foreground"
        >
          <X className="h-3.5 w-3.5" />
        </button>
        <p className="mb-2 text-[10px] uppercase tracking-widest text-primary/80">
          You've met this fear before
        </p>
        <p className="text-sm leading-relaxed text-foreground/90">
          Last time you traced it to{" "}
          <span className="text-foreground/60 line-through decoration-primary/40">
            "{resolution.limitingBeliefText}"
          </span>{" "}
          and chose{" "}
          <span className="text-primary">"{resolution.empoweringBeliefText}"</span>
          {resolution.sacredMantra ? ` — "${resolution.sacredMantra}"` : ""}.
        </p>
        <p className="mt-2 text-xs text-muted-foreground">
          Want to revisit, or go deeper with this one?
        </p>
      </div>
    </div>
  );
}

function Home() {
  const { data: user } = useAuthUser();
  const { data: billing, refetch: refetchBilling } = useBillingStatus(!!user);
  const createSession = useCreateSession();
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [slides, setSlides] = useState<Slide[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [started, setStarted] = useState(false);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [upgradeNextFreeAt, setUpgradeNextFreeAt] = useState<string | null>(
    null,
  );
  const [upgradeToast, setUpgradeToast] = useState(false);
  const [priorResolution, setPriorResolution] = useState<PriorResolution | null>(
    null,
  );
  const [showIneyaIntro, setShowIneyaIntro] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("upgraded") === "1") {
      void refetchBilling();
      setUpgradeToast(true);
      params.delete("upgraded");
      const next = params.toString();
      const url = next
        ? `${window.location.pathname}?${next}`
        : window.location.pathname;
      window.history.replaceState({}, "", url);
      const timer = setTimeout(() => setUpgradeToast(false), 5000);
      return () => clearTimeout(timer);
    }
  }, [refetchBilling]);

  const freeStatusLine =
    billing?.plan !== "free"
      ? null
      : billing?.canStartSession
        ? "1 free session available this week"
        : formatNextFreeAt(billing?.nextFreeAt)
          ? `Next free session: ${formatNextFreeAt(billing?.nextFreeAt)}`
          : null;

  useEffect(() => {
    if (!started || sessionId || showUpgrade) return;
    setIsLoading(true);
    createSession.mutate(undefined, {
      onSuccess: (data) => {
        setSessionId(data.session.id);
        setSlides([{ id: data.session.id, stage: data.ai.stage, ai: data.ai }]);
        setShowIneyaIntro(true);
        setIsLoading(false);
        setError(null);
        void refetchBilling();
      },
      onError: (err) => {
        setIsLoading(false);
        if (err instanceof SessionLimitReachedError) {
          setShowUpgrade(true);
          setUpgradeNextFreeAt(formatNextFreeAt(err.nextFreeAt));
          setStarted(false);
          setError(null);
          return;
        }
        if (err instanceof SessionDailyCapError) {
          setStarted(false);
          setError("You've reached today's session limit. Rest, integrate, and return tomorrow.");
          return;
        }
        setError(err.message);
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [started, showUpgrade]);

  useEffect(() => {
    if (slides.length > 0) {
      // Scroll to the intro first, then to the latest slide once the user continues
      const timer = setTimeout(() => {
        containerRef.current?.scrollTo({
          top: showIneyaIntro ? 0 : containerRef.current.scrollHeight,
          behavior: "smooth",
        });
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [slides.length, showIneyaIntro]);

  const continueSession = useContinueSession(sessionId);

  const handleResponse = (response: string) => {
    if (!sessionId || isLoading || !response.trim()) return;

    setIsLoading(true);
    setError(null);

    // Mark current slide with user response
    setSlides((prev) => {
      const next = [...prev];
      next[next.length - 1] = {
        ...next[next.length - 1],
        userResponse: response,
      };
      return next;
    });

    continueSession.mutate(
      { response },
      {
        onSuccess: (data) => {
          setSlides((prev) => [
            ...prev,
            {
              id: `${sessionId}-${prev.length}`,
              stage: data.ai.stage,
              ai: data.ai,
            },
          ]);
          setPriorResolution(data.priorResolution ?? null);
          setIsLoading(false);
        },
        onError: (err) => {
          setIsLoading(false);
          setError(err.message);
        },
      },
    );
  };

  const handleRestart = () => {
    setSessionId(null);
    setSlides([]);
    setStarted(false);
    setShowUpgrade(false);
    setUpgradeNextFreeAt(null);
    setPriorResolution(null);
    setShowIneyaIntro(false);
    void refetchBilling();
  };

  const handleWaitForFree = () => {
    setShowUpgrade(false);
    setStarted(false);
    setUpgradeNextFreeAt(null);
  };

  if (!user) {
    return (
      <div className="flex min-h-[100dvh] flex-col items-center justify-center px-6 pb-[max(1.5rem,env(safe-area-inset-bottom))] pt-[calc(env(safe-area-inset-top,0px)+var(--nav-height))] text-center">
        <div className="mb-6">
          <LogoMark size="xl" />
        </div>
        <h1 className="mb-3 font-serif text-4xl font-light text-foreground">
          Ineya
        </h1>
        <p className="mb-8 max-w-sm text-muted-foreground">
          Sign in to begin transforming the beliefs that shape your reality.
        </p>
        <Link
          to="/login"
          className="inline-flex items-center gap-2 rounded-full bg-white/10 px-6 py-3 text-sm font-medium text-primary backdrop-blur-sm transition-all hover:bg-white/[0.14] hover:shadow-[0_0_24px_rgba(180,160,220,0.2)]"
        >
          Sign in
        </Link>
      </div>
    );
  }

  const currentStage = slides[slides.length - 1]?.stage ?? "own_fear";
  const showProgress = started && !showUpgrade && !showIneyaIntro;

  return (
    <div className="relative h-dvh overflow-hidden">
      {showProgress && (
        <div className="pointer-events-none fixed inset-x-0 top-0 z-40 flex justify-center px-4 pb-2 pt-[calc(env(safe-area-inset-top,0px)+var(--nav-height))] md:px-6 md:pb-3 md:pt-20">
          <Progress stage={currentStage} />
        </div>
      )}
      <ReelContainer containerRef={containerRef}>
        {showUpgrade ? (
          <UpgradeSlide
            nextFreeAtLabel={upgradeNextFreeAt}
            onWait={handleWaitForFree}
          />
        ) : !started ? (
          <IntroSlide
            onStart={() => setStarted(true)}
            disabled={isLoading}
            freeStatusLine={freeStatusLine}
          />
        ) : showIneyaIntro ? (
          <IneyaIntroSlide
            onContinue={() => setShowIneyaIntro(false)}
            disabled={isLoading}
          />
        ) : (
          <>
            {slides.map((slide, index) => {
              const isLast = index === slides.length - 1;
              const key = `${slide.id}-${index}`;

              if (slide.stage === "own_fear") {
                return (
                  <InputSlide
                    key={key}
                    ai={slide.ai}
                    onSubmit={handleResponse}
                    disabled={!isLast || isLoading}
                  />
                );
              }

              if (
                slide.stage === "amplify_fear" ||
                slide.stage === "discover_belief" ||
                slide.stage === "see_through" ||
                slide.stage === "choose_belief"
              ) {
                return (
                  <OptionsSlide
                    key={key}
                    ai={slide.ai}
                    onSubmit={handleResponse}
                    disabled={!isLast || isLoading}
                  />
                );
              }

              if (slide.stage === "embody") {
                return (
                  <EmbodySlide
                    key={key}
                    ai={slide.ai}
                    onSubmit={() => handleResponse("I am ready to live this.")}
                    disabled={!isLast || isLoading}
                  />
                );
              }

              return (
                <CompleteSlide
                  key={key}
                  ai={slide.ai}
                  onRestart={handleRestart}
                />
              );
            })}
            {isLoading && (
              <Slide>
                <div className="flex items-center gap-2 text-primary/80">
                  <div className="h-2 w-2 animate-bounce rounded-full bg-primary" />
                  <div className="h-2 w-2 animate-bounce rounded-full bg-primary [animation-delay:0.1s]" />
                  <div className="h-2 w-2 animate-bounce rounded-full bg-primary [animation-delay:0.2s]" />
                </div>
              </Slide>
            )}
          </>
        )}
      </ReelContainer>

      {error && (
        <div className="fixed bottom-[max(1.5rem,env(safe-area-inset-bottom))] left-1/2 z-50 max-w-sm -translate-x-1/2 rounded-full border border-destructive/30 bg-destructive/10 px-5 py-2.5 text-center text-xs text-destructive-foreground backdrop-blur-sm">
          {error}
        </div>
      )}

      {upgradeToast && (
        <div className="fixed bottom-[max(1.5rem,env(safe-area-inset-bottom))] left-1/2 z-50 max-w-sm -translate-x-1/2 rounded-full border border-primary/30 bg-primary/10 px-5 py-2.5 text-center text-xs text-primary backdrop-blur-sm">
          Welcome to Premium — transform whenever fear knocks.
        </div>
      )}

      {!error && priorResolution && (
        <PriorResolutionBanner
          resolution={priorResolution}
          onDismiss={() => setPriorResolution(null)}
        />
      )}
    </div>
  );
}
